import { useEffect, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router';

import useWindowBeforeUnload from './useWindowBeforeUnload';

interface AllowedLocation {
    pathname: string;
    search: string;
}

interface UploadNavigationGuard {
    warningOpen: boolean;
    confirmCancelAll: () => Promise<void>;
    dismiss: () => void;
}

/**
 * Hard-blocks navigation away from the current files page while uploads are
 * active. The guard is anchored to the pathname the upload started on (My
 * Files, Shared with Me, or All Files — uploads are possible on any of them
 * via folders with the `add_files` option), not hardcoded to `/my-files`.
 *
 * Browser refresh / tab close is handled with the native `beforeunload`
 * prompt. In-app navigation cannot use react-router's `useBlocker` (the app
 * mounts a plain `BrowserRouter`, not a data router), so any attempt to leave
 * the anchored route is reverted and a warning dialog is surfaced. The user
 * must explicitly cancel all active uploads before the navigation proceeds.
 */
const useUploadNavigationGuard = (
    isActive: boolean,
    onCancelAll: () => Promise<void>,
): UploadNavigationGuard => {
    const location = useLocation();
    const navigate = useNavigate();

    const lastAllowedRef = useRef<AllowedLocation>({
        pathname: location.pathname,
        search: location.search,
    });
    const [pending, setPending] = useState<AllowedLocation | null>(null);

    useWindowBeforeUnload(() => isActive);

    useEffect(() => {
        // While the warning dialog is open we already reverted; ignore changes.
        if (pending) {
            return;
        }
        // Same-pathname changes (e.g. folder navigation via search params) are
        // allowed; only leaving the page the upload started on is blocked.
        if (!isActive || location.pathname === lastAllowedRef.current.pathname) {
            lastAllowedRef.current = { pathname: location.pathname, search: location.search };
            return;
        }
        // Navigation leaving the current files page while uploading: revert and warn.
        setPending({ pathname: location.pathname, search: location.search });
        navigate(
            { pathname: lastAllowedRef.current.pathname, search: lastAllowedRef.current.search },
            { replace: true },
        );
    }, [location, isActive, pending, navigate]);

    const confirmCancelAll = async (): Promise<void> => {
        const target = pending;
        await onCancelAll();
        setPending(null);
        if (target) {
            navigate({ pathname: target.pathname, search: target.search });
        }
    };

    const dismiss = (): void => {
        setPending(null);
    };

    return { warningOpen: !!pending, confirmCancelAll, dismiss };
};

export default useUploadNavigationGuard;
